import { ToolTradeMapping, type ToolTradeMappingList } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { InventoryClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type ToolTradeMappingFilter = EntityFilter<ToolTradeMapping>;

export const useBatchGetToolTradeMappings = <TSelectData = ToolTradeMappingList,>({
  filter = {},
  enabled = true,
  select,
}: {
  filter?: ToolTradeMappingFilter;
  enabled?: boolean;
  select?: (data: ToolTradeMappingList) => TSelectData;
} = {}) => {
  return useQuery({
    queryKey: [
      queryKeys.toolTradeMappings.root,
      queryKeys.toolTradeMappings.list,
      queryKeys.toolTradeMappings.getHash(filter),
    ],
    queryFn: async () => {
      return InventoryClientService.BatchGetToolTradeMapping(ToolTradeMapping.create(filter));
    },
    enabled,
    select,
  });
};

export const useToolTradeMappingSyncMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      catalogItemId,
      tradeTypeIds,
    }: {
      catalogItemId: number;
      tradeTypeIds: number[];
    }) => {
      const existing = await InventoryClientService.BatchGetToolTradeMapping(
        ToolTradeMapping.create({ catalogItemId }),
      );
      const existingMappings = existing.results ?? [];
      const existingTradeIds = existingMappings.map((mapping) => mapping.tradeTypeId);

      const toCreate = tradeTypeIds.filter((id) => !existingTradeIds.includes(id));
      const toDelete = existingMappings.filter(
        (mapping) => !tradeTypeIds.includes(mapping.tradeTypeId),
      );

      await Promise.all([
        ...toCreate.map((tradeTypeId) =>
          InventoryClientService.CreateToolTradeMapping(
            ToolTradeMapping.create({ catalogItemId, tradeTypeId }),
          ),
        ),
        ...toDelete.map((mapping) =>
          InventoryClientService.DeleteToolTradeMapping(
            ToolTradeMapping.create({ id: mapping.id }),
          ),
        ),
      ]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.toolTradeMappings.root] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.catalogItems.root] });
    },
  });
};
